import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  BatteryChargingFull as CapacityIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Timeline as TimelineIcon,
  Visibility,
  Edit,
  Close,
  Save,
  Add,
  PlayArrow,
  Pause,
  Settings,
  TrendingUp,
  SwapVert,
  Power as PowerIcon,
  WbSunny as EcoIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';

const Capacity = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    status: '',
    searchTerm: '',
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [statusConfirmDialogOpen, setStatusConfirmDialogOpen] = useState(false);
  const [selectedCapacity, setSelectedCapacity] = useState(null);
  const [selectedForStatus, setSelectedForStatus] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [editFormData, setEditFormData] = useState({
    capacityName: '',
    capacityValue: '',
    unit: 'kW',
    type: 'Solar',
    efficiency: '',
    description: '',
    isActive: true,
    installationDate: '',
    warrantyPeriod: '',
    manufacturer: '',
    model: '',
  });

  const sampleData = [
    { 
      id: 1, 
      capacityName: 'Solar Panel Array A', 
      capacityValue: 5.5, 
      unit: 'kW',
      type: 'Solar',
      efficiency: 22.5,
      description: 'High-efficiency monocrystalline solar panels',
      isActive: true,
      installationDate: '2024-01-15',
      warrantyPeriod: '25 years',
      manufacturer: 'SunPower',
      model: 'SPR-X22-360',
      totalEnergy: 1250,
      generatedEnergy: 1180,
      efficiencyRate: 94.4,
    },
    { 
      id: 2, 
      capacityName: 'Wind Turbine B', 
      capacityValue: 3.2, 
      unit: 'kW',
      type: 'Wind',
      efficiency: 35.8,
      description: 'Small-scale wind energy generator',
      isActive: true,
      installationDate: '2024-02-20',
      warrantyPeriod: '20 years',
      manufacturer: 'Vestas',
      model: 'V100-2.0MW',
      totalEnergy: 890,
      generatedEnergy: 756,
      efficiencyRate: 84.9,
    },
    { 
      id: 3, 
      capacityName: 'Battery Storage C', 
      capacityValue: 10.0, 
      unit: 'kWh',
      type: 'Storage',
      efficiency: 95.2,
      description: 'Lithium-ion battery storage system',
      isActive: true,
      installationDate: '2024-01-30',
      warrantyPeriod: '10 years',
      manufacturer: 'Tesla',
      model: 'Powerwall 2',
      totalEnergy: 0,
      generatedEnergy: 0,
      efficiencyRate: 95.2,
    },
    { 
      id: 4, 
      capacityName: 'Hydro Generator D', 
      capacityValue: 2.8, 
      unit: 'kW',
      type: 'Hydro',
      efficiency: 78.5,
      description: 'Micro-hydroelectric power generator',
      isActive: true,
      installationDate: '2024-03-10',
      warrantyPeriod: '15 years',
      manufacturer: 'Gilkes',
      model: 'GH-100',
      totalEnergy: 420,
      generatedEnergy: 330,
      efficiencyRate: 78.6,
    },
    { 
      id: 5, 
      capacityName: 'Solar Panel Array E', 
      capacityValue: 8.0, 
      unit: 'kW',
      type: 'Solar',
      efficiency: 20.8,
      description: 'Polycrystalline solar panel installation',
      isActive: false,
      installationDate: '2023-11-05',
      warrantyPeriod: '25 years',
      manufacturer: 'Canadian Solar',
      model: 'CS6K-300MS',
      totalEnergy: 2100,
      generatedEnergy: 1890,
      efficiencyRate: 90.0,
    },
    { 
      id: 6, 
      capacityName: 'Geothermal Heat Pump F', 
      capacityValue: 4.5, 
      unit: 'kW',
      type: 'Geothermal',
      efficiency: 400.0,
      description: 'Ground-source heat pump system',
      isActive: true,
      installationDate: '2024-02-28',
      warrantyPeriod: '12 years',
      manufacturer: 'WaterFurnace',
      model: 'Envision Series 7',
      totalEnergy: 0,
      generatedEnergy: 0,
      efficiencyRate: 400.0,
    },
  ];

  const [data, setData] = useState(sampleData);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Edit form handlers
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = () => {
    // Determine if we're creating or updating
    if (selectedCapacity) {
      handleUpdateCapacity();
    } else {
      handleCreateCapacity();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilters({ ...filters, searchTerm: '' });
    setHasSearched(false);
    setPage(0);
  };

  // New Capacity handler
  const handleNewCapacity = () => {
    console.log('Creating new capacity...');
    // Reset form data for new capacity
    setEditFormData({
      capacityName: '',
      capacityValue: '',
      unit: 'kW',
      type: 'Solar',
      efficiency: '',
      description: '',
      isActive: true,
      installationDate: '',
      warrantyPeriod: '',
      manufacturer: '',
      model: '',
    });
    setSelectedCapacity(null);
    setEditDialogOpen(true);
  };

  // Create new capacity
  const handleCreateCapacity = () => {
    if (!editFormData.capacityName || !editFormData.capacityValue) {
      alert('Please fill in required fields: Capacity Name and Capacity Value');
      return;
    }

    // Create new capacity object
    const newCapacity = {
      id: Date.now(), // Simple ID generation
      capacityName: editFormData.capacityName,
      capacityValue: parseFloat(editFormData.capacityValue),
      unit: editFormData.unit,
      type: editFormData.type,
      efficiency: parseFloat(editFormData.efficiency) || 0,
      description: editFormData.description,
      isActive: editFormData.isActive,
      installationDate: editFormData.installationDate,
      warrantyPeriod: editFormData.warrantyPeriod,
      manufacturer: editFormData.manufacturer,
      model: editFormData.model,
      totalEnergy: 0,
      generatedEnergy: 0,
      efficiencyRate: parseFloat(editFormData.efficiency) || 0,
    };

    console.log('Creating new capacity:', newCapacity);
    
    // Here you would typically make an API call to save the new capacity
    // For demo purposes, we'll just close the dialog and show success message
    setData(prev => [newCapacity, ...prev]);
    alert(`New capacity "${newCapacity.capacityName}" created successfully!`);
    
    // Close dialog and reset form
    setEditDialogOpen(false);
    setSelectedCapacity(null);
    setEditFormData({
      capacityName: '',
      capacityValue: '',
      unit: 'kW',
      type: 'Solar',
      efficiency: '',
      description: '',
      isActive: true,
      installationDate: '',
      warrantyPeriod: '',
      manufacturer: '',
      model: '',
    });
  };

  // Update existing capacity
  const handleUpdateCapacity = () => {
    if (!editFormData.capacityName || !editFormData.capacityValue) {
      alert('Please fill in required fields: Capacity Name and Capacity Value');
      return;
    }

    const updatedCapacity = {
      ...selectedCapacity,
      capacityName: editFormData.capacityName,
      capacityValue: parseFloat(editFormData.capacityValue),
      unit: editFormData.unit,
      type: editFormData.type,
      efficiency: parseFloat(editFormData.efficiency) || 0,
      description: editFormData.description,
      isActive: editFormData.isActive,
      installationDate: editFormData.installationDate,
      warrantyPeriod: editFormData.warrantyPeriod,
      manufacturer: editFormData.manufacturer,
      model: editFormData.model,
    };

    console.log('Updating capacity:', updatedCapacity);
    
    // Here you would typically make an API call to update the capacity
    // For demo purposes, we'll just close the dialog and show success message
    setData(prev => prev.map(i => i.id === updatedCapacity.id ? updatedCapacity : i));
    alert(`Capacity "${updatedCapacity.capacityName}" updated successfully!`);
    
    // Close dialog and reset form
    setEditDialogOpen(false);
    setSelectedCapacity(null);
    setEditFormData({
      capacityName: '',
      capacityValue: '',
      unit: 'kW',
      type: 'Solar',
      efficiency: '',
      description: '',
      isActive: true,
      installationDate: '',
      warrantyPeriod: '',
      manufacturer: '',
      model: '',
    });
  };

  // Search handler - applies all filters
  const handleSearch = () => {
    setSearchLoading(true);
    setHasSearched(true);
    console.log('Search triggered with filters:', {
      searchTerm: searchTerm,
      status: filters.status,
    });
    // Simulate API call delay
    setTimeout(() => {
      setFilters(prev => ({ ...prev, searchTerm: searchTerm }));
      setSearchLoading(false);
    }, 500);
  };

  // View and Edit action handlers
  const handleView = (capacity) => {
    setSelectedCapacity(capacity);
    setViewDialogOpen(true);
  };

  const handleEdit = (capacity) => {
    setSelectedCapacity(capacity);
    setEditFormData({
      capacityName: capacity.capacityName,
      capacityValue: capacity.capacityValue.toString(),
      unit: capacity.unit,
      type: capacity.type,
      efficiency: capacity.efficiency.toString(),
      description: capacity.description,
      isActive: capacity.isActive,
      installationDate: capacity.installationDate,
      warrantyPeriod: capacity.warrantyPeriod,
      manufacturer: capacity.manufacturer,
      model: capacity.model,
    });
    setEditDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedCapacity(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedCapacity(null);
    setEditFormData({
      capacityName: '',
      capacityValue: '',
      unit: 'kW',
      type: 'Solar',
      efficiency: '',
      description: '',
      isActive: true,
      installationDate: '',
      warrantyPeriod: '',
      manufacturer: '',
      model: '',
    });
  };

  // Optimized filter function with memoization - only works when search is performed
  const getFilteredData = useCallback(() => {
    // If no search has been performed, return all data
    if (!hasSearched) {
      return data;
    }

    let filteredData = [...data];

    // Apply search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredData = filteredData.filter(item =>
        item.capacityName.toLowerCase().includes(searchLower) ||
        item.description.toLowerCase().includes(searchLower) ||
        item.manufacturer.toLowerCase().includes(searchLower) ||
        item.model.toLowerCase().includes(searchLower) ||
        item.type.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (filters.status) {
      filteredData = filteredData.filter(item => 
        filters.status === 'active' ? item.isActive : !item.isActive
      );
    }

    return filteredData;
  }, [hasSearched, filters.searchTerm, filters.status, data]);

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);
  const totalCapacities = filteredData.length;
  const activeCapacities = filteredData.filter(item => item.isActive).length;
  const totalCapacityValue = filteredData.reduce((sum, item) => sum + item.capacityValue, 0);
  const averageEfficiency = filteredData.length > 0 ? 
    (filteredData.reduce((sum, item) => sum + item.efficiencyRate, 0) / filteredData.length).toFixed(1) : 0;

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };
  const handleStatusToggle = (item) => {
    setSelectedForStatus(item);
    setStatusConfirmDialogOpen(true);
  };

  const confirmStatusToggle = () => {
    if (!selectedForStatus) return;
    setData(prev => prev.map(i => i.id === selectedForStatus.id ? { ...i, isActive: !i.isActive } : i));
    setStatusConfirmDialogOpen(false);
    setSelectedForStatus(null);
  };

  const getEfficiencyColor = (rate) => {
    if (rate >= 90) return 'success';
    if (rate >= 70) return 'warning';
    return 'error';
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Solar': return <EcoIcon />;
      case 'Wind': return <SpeedIcon />;
      case 'Storage': return <CapacityIcon />;
      case 'Hydro': return <PowerIcon />;
      case 'Geothermal': return <PowerIcon />;
      default: return <CapacityIcon />;
    }
  };

  const getTypeColor = (type) => {
    return '#536DFE'; // Simple blue color for all types
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Green Energy Capacity Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage renewable energy capacity and generation systems
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleNewCapacity}
            sx={{ 
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
            }}
          >
            New Capacity
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <CapacityIcon sx={{ fontSize: 28, color: '#536DFE', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {totalCapacities}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Systems
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <CheckCircleIcon sx={{ fontSize: 28, color: '#4caf50', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {activeCapacities}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Active Systems
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <PowerIcon sx={{ fontSize: 28, color: '#ff9800', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {totalCapacityValue.toFixed(1)} kW
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Capacity
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <TrendingUp sx={{ fontSize: 28, color: '#2196f3', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {averageEfficiency}%
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Avg Efficiency
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
            Search & Filters
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {/* Search Input */}
            <TextField
              size="small"
              placeholder="Search capacity..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      edge="end"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              sx={{
                minWidth: 300,
                maxWidth: 400,
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            
            {/* Filter Controls */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>System Status</InputLabel>
              <Select 
                label="System Status" 
                value={filters.status}
                onChange={(e) => {
                  setFilters({ ...filters, status: e.target.value });
                }}
                sx={{
                  borderRadius: 2,
                }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              disabled={searchLoading}
              startIcon={searchLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon fontSize="small" />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#536DFE',
                borderRadius: 2,
                px: 3,
                py: 1,
                '&:hover': {
                  backgroundColor: '#3F51B5',
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e',
                }
              }}
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setFilters({
                  status: '',
                  searchTerm: '',
                });
                setSearchTerm('');
                setHasSearched(false);
                setSearchLoading(false);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#536DFE',
                color: '#536DFE',
                borderRadius: 2,
                px: 3,
                py: 1,
                '&:hover': {
                  borderColor: '#3F51B5',
                  backgroundColor: '#f5f5f5',
                }
              }}
            >
              Clear All
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Status Toggle Confirmation Dialog */}
      <Dialog 
        open={statusConfirmDialogOpen} 
        onClose={() => setStatusConfirmDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ backgroundColor: '#536DFE', color: 'white', display: 'flex', alignItems: 'center', gap: 1, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            {selectedForStatus?.isActive ? 'Deactivate System' : 'Activate System'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to {selectedForStatus?.isActive ? 'deactivate' : 'activate'} the system <strong>"{selectedForStatus?.capacityName}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedForStatus?.isActive ? 'Deactivating this system will mark it as inactive.' : 'Activating this system will mark it as active.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button onClick={() => setStatusConfirmDialogOpen(false)} variant="outlined" sx={{ textTransform: 'none', px: 3, py: 1, fontWeight: 600, borderRadius: 1.5 }}>Cancel</Button>
          <Button onClick={confirmStatusToggle} variant="contained" color={selectedForStatus?.isActive ? 'warning' : 'success'} sx={{ textTransform: 'none', px: 3, py: 1, fontWeight: 600, borderRadius: 1.5 }}>
            {selectedForStatus?.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Search Results Summary */}
      {hasSearched && (
        <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, border: '1px solid #e8f5e8' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 1,
                backgroundColor: '#e8f5e8',
                borderRadius: 2,
                minWidth: 'fit-content'
              }}>
                {searchLoading ? (
                  <CircularProgress size={20} color="primary" />
                ) : (
                  <SearchIcon sx={{ color: '#536DFE' }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#536DFE' }}>
                  Search Results
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                {searchLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Searching capacity systems...
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                      {filteredData.length} system{filteredData.length !== 1 ? 's' : ''} found
                    </Typography>
                    {filters.status && filters.status !== 'All Status' && (
                      <Chip
                        label={`Status: ${filters.status}`} 
                        size="small"
                        color="success" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Capacity Systems
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>System</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Type</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Capacity</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Efficiency</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Manufacturer</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          Searching capacity systems...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length > 0 ? (
                  filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                    <TableRow 
                      key={row.id} 
                      hover
                      sx={{ 
                        '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                        '&:hover': { backgroundColor: '#e8f5e8 !important' }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: '#536DFE', width: 32, height: 32 }}>
                            {getTypeIcon(row.type)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.capacityName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.model}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={row.type}
                          size="small" 
                          sx={{ 
                            backgroundColor: '#536DFE',
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {row.capacityValue} {row.unit}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${row.efficiencyRate}%`}
                          size="small" 
                          color={getEfficiencyColor(row.efficiencyRate)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.manufacturer}</TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={row.isActive}
                              onChange={() => handleStatusToggle(row)}
                              color="success"
                              size="medium"
                            />
                          }
                          label={
                            <Chip 
                              label={row.isActive ? 'Active' : 'Inactive'}
                              size="small" 
                              color={getStatusColor(row.isActive)}
                              sx={{ fontWeight: 600 }}
                            />
                          }
                          sx={{ m: 0 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              onClick={() => handleView(row)}
                              sx={{ color: '#536DFE' }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit System">
                            <IconButton 
                              size="small" 
                              onClick={() => handleEdit(row)}
                              sx={{ color: '#ff9800' }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No capacity systems found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {filteredData.length > 0 && (
            <TablePagination
              
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog 
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: '#536DFE',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CapacityIcon />
            <Typography variant="h6" fontWeight="bold">
              Capacity System Details
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseViewDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedCapacity && (
            <>
              {/* Header Section */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 3,
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Avatar sx={{ bgcolor: '#536DFE', width: 48, height: 48 }}>
                  {getTypeIcon(selectedCapacity.type)}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedCapacity.capacityName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedCapacity.manufacturer} • {selectedCapacity.model}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip 
                      label={selectedCapacity.isActive ? 'Active' : 'Inactive'} 
                      size="small" 
                      color={getStatusColor(selectedCapacity.isActive)}
                    />
                    <Chip 
                      label={`${selectedCapacity.efficiencyRate}% Efficiency`} 
                      size="small" 
                      color={getEfficiencyColor(selectedCapacity.efficiencyRate)}
                    />
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={3}>
                {/* System Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Settings color="primary" />
                        System Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Capacity Name:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedCapacity.capacityName}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Type:</Typography>
                          <Chip 
                            label={selectedCapacity.type}
                            size="small" 
                            sx={{ 
                              backgroundColor: '#536DFE',
                              color: 'white'
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Capacity:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedCapacity.capacityValue} {selectedCapacity.unit}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Efficiency:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedCapacity.efficiency}%</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Description:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedCapacity.description}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Installation Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ScheduleIcon color="primary" />
                        Installation Details
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Manufacturer:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedCapacity.manufacturer}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Model:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedCapacity.model}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Installation Date:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedCapacity.installationDate}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Warranty Period:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedCapacity.warrantyPeriod}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Status:</Typography>
                          <Chip 
                            label={selectedCapacity.isActive ? 'Active' : 'Inactive'} 
                            size="small" 
                            color={getStatusColor(selectedCapacity.isActive)}
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Capacity Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: selectedCapacity ? '#ff9800' : '#536DFE',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedCapacity ? <Edit /> : <Add />}
            <Typography variant="h6" fontWeight="bold">
              {selectedCapacity ? 'Edit Capacity System' : 'New Capacity System'}
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseEditDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {selectedCapacity ? `Edit capacity system: ${selectedCapacity.capacityName}` : 'Create new capacity system'}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Capacity Name"
                  required
                  value={editFormData.capacityName}
                  onChange={(e) => handleEditFormChange('capacityName', e.target.value)}
                  variant="outlined"
                  size="small"
                  error={!editFormData.capacityName}
                  helperText={!editFormData.capacityName ? 'Capacity name is required' : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Capacity Value"
                  type="number"
                  required
                  value={editFormData.capacityValue}
                  onChange={(e) => handleEditFormChange('capacityValue', e.target.value)}
                  variant="outlined"
                  size="small"
                  error={!editFormData.capacityValue}
                  helperText={!editFormData.capacityValue ? 'Capacity value is required' : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Unit</InputLabel>
                  <Select
                    value={editFormData.unit}
                    onChange={(e) => handleEditFormChange('unit', e.target.value)}
                    label="Unit"
                  >
                    <MenuItem value="kW">kW</MenuItem>
                    <MenuItem value="kWh">kWh</MenuItem>
                    <MenuItem value="MW">MW</MenuItem>
                    <MenuItem value="MWh">MWh</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={editFormData.type}
                    onChange={(e) => handleEditFormChange('type', e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="Solar">Solar</MenuItem>
                    <MenuItem value="Wind">Wind</MenuItem>
                    <MenuItem value="Storage">Storage</MenuItem>
                    <MenuItem value="Hydro">Hydro</MenuItem>
                    <MenuItem value="Geothermal">Geothermal</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Efficiency (%)"
                  type="number"
                  value={editFormData.efficiency}
                  onChange={(e) => handleEditFormChange('efficiency', e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Manufacturer"
                  value={editFormData.manufacturer}
                  onChange={(e) => handleEditFormChange('manufacturer', e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Model"
                  value={editFormData.model}
                  onChange={(e) => handleEditFormChange('model', e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Installation Date"
                  type="date"
                  value={editFormData.installationDate}
                  onChange={(e) => handleEditFormChange('installationDate', e.target.value)}
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Warranty Period"
                  value={editFormData.warrantyPeriod}
                  onChange={(e) => handleEditFormChange('warrantyPeriod', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="e.g., 25 years"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={editFormData.description}
                  onChange={(e) => handleEditFormChange('description', e.target.value)}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editFormData.isActive}
                      onChange={(e) => handleEditFormChange('isActive', e.target.checked)}
                      color="success"
                    />
                  }
                  label="Active System"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseEditDialog}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit}
            variant="contained"
            startIcon={<Save />}
            sx={{ 
              backgroundColor: selectedCapacity ? '#ff9800' : '#536DFE',
              '&:hover': { backgroundColor: selectedCapacity ? '#f57c00' : '#3F51B5' }
            }}
          >
            {selectedCapacity ? 'Save Changes' : 'Create System'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Capacity;
